/**
 * Dunkle Aktionsleiste unten (Board „Preisautomatik · Einstellungen“): Stand in einem Satz,
 * „Änderungen verwerfen“ und die eine Hauptaktion – „Wirkung prüfen“ oder bei „Aus“ „Aus speichern“.
 * Scheitert eine Anfrage, steht der Grund hier, wo geklickt wurde.
 */
import { Loader2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

interface Props {
  title: string;
  subline: string;
  /** Grund einer gescheiterten Prüfung oder Speicherung. */
  problem?: string | null;
  /** „Neuen Stand laden“, wenn der Grund ein neuerer Stand auf dem Server ist. */
  onReload?: () => void;
  off: boolean;
  changed: boolean;
  busy: boolean;
  onDiscard: () => void;
  onPrimary: () => void;
}

export function ConfigurationActionBar({ title, subline, off, changed, busy, ...props }: Props) {
  const enabled = changed && !busy;
  return (
    // Am Handy (Board „Einstellungen · Handy“) nur die zwei Knöpfe; ohne Änderung nur der Stand.
    <div className="bg-ink rounded-tile shadow-float sticky bottom-0 z-20 mt-2 grid grid-cols-[1fr_1.4fr] items-center gap-2 p-2.5 text-white md:bottom-4 md:flex md:flex-wrap md:gap-2.5 md:px-[18px] md:py-3.5">
      <span
        role="status"
        className={cn(
          'col-span-2 flex min-w-0 grow flex-col px-1 md:px-0',
          changed && 'sr-only md:not-sr-only',
        )}
      >
        <span className="text-[15px] font-bold">{title}</span>
        <span className="text-on-forest-soft hidden text-xs md:block">{subline}</span>
      </span>
      <button
        type="button"
        disabled={!enabled}
        onClick={props.onDiscard}
        aria-label="Änderungen verwerfen"
        className={cn(
          'rounded-pill h-[46px] border border-white/25 px-[18px] text-sm font-bold text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40',
          !changed && 'hidden md:block',
        )}
      >
        <span className="md:hidden">Verwerfen</span>
        <span className="hidden md:inline">Änderungen verwerfen</span>
      </button>
      <button
        type="button"
        disabled={!enabled}
        aria-busy={busy}
        onClick={props.onPrimary}
        className={cn(
          'rounded-pill flex h-[46px] items-center justify-center gap-2 px-[18px] text-sm font-bold',
          enabled ? 'bg-mint text-forest-deep hover:bg-white' : 'bg-white/10 text-white/50',
          !changed && 'hidden md:flex',
        )}
      >
        {busy && <Loader2 size={16} aria-hidden className="animate-spin" />}
        {off ? 'Aus speichern' : 'Wirkung prüfen'}
      </button>
      {props.problem && (
        <p
          role="alert"
          className="text-sealwax-on-dark col-span-2 flex w-full flex-wrap items-center gap-2 border-t border-white/15 px-1 pt-2.5 text-[13px] font-semibold md:px-0"
        >
          <span className="min-w-0 grow">{props.problem}</span>
          {props.onReload && (
            <button
              type="button"
              onClick={props.onReload}
              className="rounded-pill text-forest-deep min-h-11 bg-white px-4 text-[13px] font-bold"
            >
              Neuen Stand laden
            </button>
          )}
        </p>
      )}
    </div>
  );
}
